import { Box, DialogContent } from '@mui/material'
import React from 'react'
import { z } from 'zod'

import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog-header'
import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import { APIKey, UpdateAPIKeyPayload } from '@/app/module/user/types'

type Props = {
  apiKey: APIKey
  loading: boolean

  onClose: () => void
  onSubmit: (apiKey: APIKey, values: UpdateAPIKeyPayload) => Promise<void>
}

type UpdateAPIKeyValues = z.infer<typeof schema>

const ApiKeyModal: React.FC<Props> = ({ apiKey, loading, onClose, onSubmit }) => {
  const defaultValues = React.useMemo<UpdateAPIKeyValues>(
    () => ({
      active: apiKey.active,
      expires: apiKey.expires,
      name: apiKey.name,
    }),
    [apiKey],
  )

  const handleSubmit = React.useCallback(
    async (values: UpdateAPIKeyValues) => {
      await onSubmit(apiKey, values as UpdateAPIKeyPayload)
      onClose()
      return true
    },
    [apiKey, onClose, onSubmit],
  )

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader icon="key" title="Manage PAT Details" onClose={onClose} />
      <DialogContent>
        <Form
          defaultValues={defaultValues}
          fields={fields}
          schema={schema}
          onSubmit={handleSubmit}
          renderActions={({ isDirty }) => (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <SubmitButton isDirty={isDirty} isSubmitting={loading}>
                Save
              </SubmitButton>
            </Box>
          )}
        />
      </DialogContent>
    </Dialog>
  )
}

const fields = {
  name: {
    type: 'text',
    fieldProps: {
      label: 'Name',
      name: 'name',
    },
  },
  expires: {
    type: 'datetime',
    fieldProps: {
      label: 'Expires',
      name: 'expires',
    },
  },
  active: {
    type: 'switch',
    fieldProps: {
      label: 'Enabled',
      name: 'active',
      sx: {
        marginLeft: '-12px',
      },
    },
  },
} as const

const schema = z.object({
  name: z.string().optional().describe('Name'),
  expires: z.string().optional().describe('Expires'),
  active: z.boolean().optional().describe('Enabled'),
})

export default ApiKeyModal
